import {
  ComponentDefinition,
  ComponentConfig,
} from "@/types/models/componentConfig";
import { apiClient } from "@/lib/apiClient";

/**
 * Service for fetching component definitions from the backend
 */
class ComponentService {
  private cache: Map<string, ComponentDefinition[]> = new Map();
  private cacheTimestamp: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch all component definitions for a store
   */
  async getComponents(storeId: string): Promise<ComponentDefinition[]> {
    // Check cache first
    const cacheKey = storeId;
    const cached = this.cache.get(cacheKey);
    const timestamp = this.cacheTimestamp.get(cacheKey);

    if (cached && timestamp && Date.now() - timestamp < this.CACHE_DURATION) {
      return cached;
    }

    const response = await apiClient.get<{ components: ComponentDefinition[] }>(
      `/api/v1/admin/:storeId/web-builder/components`,
    );

    const components = response.data.components || [];

    // Update cache
    this.cache.set(cacheKey, components);
    this.cacheTimestamp.set(cacheKey, Date.now());

    return components;
  }

  /**
   * Extract component definitions from actual section data
   * This analyzes the section's component_data to infer what inputs are available
   */
  extractComponentDefinitionsFromSections(
    sections: any[],
  ): ComponentDefinition[] {
    const definitionsMap = new Map<string, ComponentDefinition>();

    sections.forEach((section) => {
      const componentData = section.component_data || {};

      Object.values(componentData).forEach((comp: any) => {
        const slug = comp.componentSlug;
        if (!slug || definitionsMap.has(slug)) return;

        // Extract config from desktop data (base data)
        const desktopData = Array.isArray(comp.desktop) ? comp.desktop : [];
        const config: ComponentConfig[] = desktopData.map((input: any) => ({
          title: input.inputKey || "unknown",
          description: `${input.inputKey} configuration`,
          type: input.type || "text",
          accepted_values: null,
          accepted_range: null,
        }));

        definitionsMap.set(slug, {
          id: `${slug}-extracted`,
          name: comp.name || slug,
          slug: slug,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          config: config,
        });
      });
    });

    const extracted = Array.from(definitionsMap.values());
    console.log(
      "📦 Extracted component definitions from sections:",
      extracted.length,
    );
    return extracted;
  }

  /**
   * Clear the cache
   */
  clearCache(storeId?: string) {
    if (storeId) {
      this.cache.delete(storeId);
      this.cacheTimestamp.delete(storeId);
    } else {
      this.cache.clear();
      this.cacheTimestamp.clear();
    }
  }

  /**
   * Get a component by slug
   */
  async getComponentBySlug(
    storeId: string,
    slug: string,
  ): Promise<ComponentDefinition | undefined> {
    const components = await this.getComponents(storeId);
    return components.find((c) => c.slug === slug);
  }
}

export const componentService = new ComponentService();
export default componentService;
